import moment from 'moment';
import { isLocalHost, isSupportedLanguage } from '@/common/utils/language';

const COOKIE_CONFIG = {
	AUTH_TOKEN: {
		name: 'softai_app_token',
		maxAge: 60 * 60 * 24, // 1 day
	},
	REFRESH_TOKEN: {
		name: 'softai_app_refresh_token',
		maxAge: 60 * 60 * 24 * 365, // 1 year
	},
	QUIZ_STUDIO_TRANSFER: {
		name: 'softai_quiz_studio_transfer',
		maxAge: 60 * 5, // 5 minutes
	},
};

const getSharedCookieDomain = () => {
	const configuredDomain = import.meta.env.VITE_AUTH_COOKIE_DOMAIN?.trim();

	if (configuredDomain) {
		return configuredDomain.startsWith('.')
			? configuredDomain
			: `.${configuredDomain}`;
	}

	const hostname = window.location.hostname;

	if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
		return '.localhost';
	}

	if (isLocalHost()) {
		return undefined;
	}

	const hostnameParts = hostname.split('.');

	if (hostnameParts.length < 2) {
		return undefined;
	}

	const domainParts = isSupportedLanguage(hostnameParts[0])
		? hostnameParts.slice(1)
		: hostnameParts;

	if (domainParts.length < 2) {
		return undefined;
	}

	return `.${domainParts.join('.')}`;
};

const STORAGE_KEYS = {
	STUDIO_STATE: 'studio',
	UTM_CAMPAIGN: 'utm_campaign',
	UTM_MEDIUM: 'utm_medium',
	UTM_SOURCE: 'utm_source',
	UTM_TERM: 'utm_term',
	UTM_CONTENT: 'utm_content',
	FBCLID: 'fbclid',
	UID: 'uid',
	MSCLKID: 'msclkid',
	BROWSING_PERSON: 'browsingPerson',
	STUDIO_RESTORE_FLAG: 'studioPendingRestore',
	QUIZ_AUTO_GENERATE_FLAG: 'quizAutoGeneratePending',
	TOKEN_PAYMENT_BASELINE: 'token_payment_baseline',
	TOKEN_PAYMENT_ERROR: 'token_payment_error',
	USER_ID: 'softai_app_user_id',
	USER: 'user',
	AUTH_TOKEN: 'softai_app_token',
	AUTH_REFRESH_TOKEN: 'softai_app_refresh_token',
};

class StorageManager {
	constructor() {
		if (StorageManager.instance) {
			return StorageManager.instance;
		}
		StorageManager.instance = this;
	}

	_buildCookie(name, value, maxAgeSeconds) {
		const encodedValue = encodeURIComponent(value);
		let cookie = `${name}=${encodedValue}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;

		const domain = getSharedCookieDomain();
		if (domain) {
			cookie += `; domain=${domain}`;
		}

		if (window.location.protocol === 'https:') {
			cookie += '; Secure';
		}

		return cookie;
	}

	_readCookie(name) {
		const pattern = new RegExp(`(?:^|; )${name}=([^;]*)`);
		const match = document.cookie.match(pattern);
		return match ? decodeURIComponent(match[1]) : null;
	}

	_deleteCookie(name) {
		let cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;

		const domain = getSharedCookieDomain();
		if (domain) {
			cookie += `; domain=${domain}`;
		}

		document.cookie = cookie;
	}

	_writeCookie(name, value, maxAgeSeconds) {
		document.cookie = this._buildCookie(name, value, maxAgeSeconds);
	}

	_readJsonCookie(name) {
		try {
			const value = this._readCookie(name);
			return value ? JSON.parse(value) : null;
		} catch {
			return null;
		}
	}

	_writeJsonCookie(name, data, maxAgeSeconds) {
		this._writeCookie(name, JSON.stringify(data), maxAgeSeconds);
	}

	_readLocal(key) {
		try {
			const value = localStorage.getItem(key);
			if (!value || value === 'null' || value === 'undefined') {
				return null;
			}
			return value;
		} catch {
			return null;
		}
	}

	_writeLocal(key, value) {
		try {
			localStorage.setItem(key, value);
		} catch (error) {
			console.error(`Failed to write to localStorage: ${key}`, error);
		}
	}

	_removeLocal(key) {
		try {
			localStorage.removeItem(key);
		} catch {
			// Silent fail
		}
	}

	_readJsonLocal(key) {
		try {
			const raw = this._readLocal(key);
			return raw ? JSON.parse(raw) : null;
		} catch {
			return null;
		}
	}

	_writeJsonLocal(key, data) {
		try {
			this._writeLocal(key, JSON.stringify(data));
		} catch (error) {
			console.error(`Failed to write JSON to localStorage: ${key}`, error);
		}
	}

	_readSession(key) {
		try {
			return sessionStorage.getItem(key);
		} catch {
			return null;
		}
	}

	_writeSession(key, value) {
		try {
			sessionStorage.setItem(key, value);
		} catch (error) {
			console.error(`Failed to write to sessionStorage: ${key}`, error);
		}
	}

	_removeSession(key) {
		try {
			sessionStorage.removeItem(key);
		} catch {
			// Silent fail
		}
	}

	_parseJwt(token) {
		try {
			const base64Url = token.split('.')[1];

			if (!base64Url) {
				return null;
			}

			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split('')
					.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
					.join(''),
			);
			return JSON.parse(jsonPayload);
		} catch {
			return null;
		}
	}

	_hydrateAuthFromCookies() {
		const cookieToken = this._readCookie(COOKIE_CONFIG.AUTH_TOKEN.name);
		const cookieRefreshToken = this._readCookie(
			COOKIE_CONFIG.REFRESH_TOKEN.name,
		);

		if (cookieToken && !this._readLocal(STORAGE_KEYS.AUTH_TOKEN)) {
			this._writeLocal(STORAGE_KEYS.AUTH_TOKEN, cookieToken);
		}

		if (cookieRefreshToken && !this._readLocal(STORAGE_KEYS.AUTH_REFRESH_TOKEN)) {
			this._writeLocal(STORAGE_KEYS.AUTH_REFRESH_TOKEN, cookieRefreshToken);
		}
	}

	auth = {
		getToken: () => {
			const localToken = this._readLocal(STORAGE_KEYS.AUTH_TOKEN);
			if (localToken) {
				return localToken;
			}

			const cookieToken = this._readCookie(COOKIE_CONFIG.AUTH_TOKEN.name);
			if (cookieToken) {
				this._writeLocal(STORAGE_KEYS.AUTH_TOKEN, cookieToken);
				return cookieToken;
			}

			return null;
		},

		setToken: (token, axiosInstance = null) => {
			if (!token) {
				this._deleteCookie(COOKIE_CONFIG.AUTH_TOKEN.name);
				this._removeLocal(STORAGE_KEYS.AUTH_TOKEN);

				if (axiosInstance) {
					delete axiosInstance.defaults.headers.common['authorization'];
				}
				return;
			}

			this._writeLocal(STORAGE_KEYS.AUTH_TOKEN, token);
			this._writeCookie(
				COOKIE_CONFIG.AUTH_TOKEN.name,
				token,
				COOKIE_CONFIG.AUTH_TOKEN.maxAge,
			);

			if (axiosInstance) {
				axiosInstance.defaults.headers.common['authorization'] = `Bearer ${token}`;
			}

			const payload = this._parseJwt(token);
			if (payload?.id) {
				this._writeLocal(STORAGE_KEYS.USER_ID, payload.id);
			}
		},

		getRefreshToken: () => {
			const localRefreshToken = this._readLocal(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
			if (localRefreshToken) {
				return localRefreshToken;
			}

			const cookieRefreshToken = this._readCookie(
				COOKIE_CONFIG.REFRESH_TOKEN.name,
			);
			if (cookieRefreshToken) {
				this._writeLocal(STORAGE_KEYS.AUTH_REFRESH_TOKEN, cookieRefreshToken);
				return cookieRefreshToken;
			}

			return null;
		},

		setRefreshToken: (token) => {
			if (!token) {
				this._deleteCookie(COOKIE_CONFIG.REFRESH_TOKEN.name);
				this._removeLocal(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
				return;
			}

			this._writeLocal(STORAGE_KEYS.AUTH_REFRESH_TOKEN, token);
			this._writeCookie(
				COOKIE_CONFIG.REFRESH_TOKEN.name,
				token,
				COOKIE_CONFIG.REFRESH_TOKEN.maxAge,
			);
		},

		getUserId: () => {
			return this._readLocal(STORAGE_KEYS.USER_ID);
		},

		clearAuth: (axiosInstance = null) => {
			this._deleteCookie(COOKIE_CONFIG.AUTH_TOKEN.name);
			this._deleteCookie(COOKIE_CONFIG.REFRESH_TOKEN.name);
			this._removeLocal(STORAGE_KEYS.AUTH_TOKEN);
			this._removeLocal(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
			this._removeLocal(STORAGE_KEYS.USER_ID);
			this._removeLocal(STORAGE_KEYS.USER);

			if (axiosInstance) {
				delete axiosInstance.defaults.headers.common['authorization'];
			}
		},

		isTokenExpired: (token) => {
			const payload = this._parseJwt(token);
			if (!payload?.exp) {
				return true;
			}

			const expDate = moment.unix(payload.exp);
			const today = moment();
			return expDate.isBefore(today);
		},

		refreshToken: async (axiosInstance) => {
			const token = this.auth.getToken();
			const refreshToken = this.auth.getRefreshToken();

			if (!token || !refreshToken) {
				return false;
			}

			try {
				const refreshData = await axiosInstance.post('/auth/refresh-token', {
					token,
					refreshToken,
				});

				this.auth.setToken(refreshData.data.token, axiosInstance);
				this.auth.setRefreshToken(refreshData.data.refreshToken);
				return true;
			} catch {
				return false;
			}
		},

		isTokenActive: async (axiosInstance) => {
			const token = this.auth.getToken();

			if (!token) {
				return false;
			}

			if (this.auth.isTokenExpired(token)) {
				return await this.auth.refreshToken(axiosInstance);
			}

			return true;
		},

		appendTransferToUrl: (url, options = {}) => {
			const token = options.token || this.auth.getToken();
			const refreshToken = options.refreshToken || this.auth.getRefreshToken();

			if (!token) {
				return url;
			}

			const payload = btoa(JSON.stringify({ token, refreshToken: refreshToken || '' }));
			const hashIndex = url.indexOf('#');
			const baseUrl = hashIndex >= 0 ? url.slice(0, hashIndex) : url;

			return `${baseUrl}#softai_auth=${encodeURIComponent(payload)}`;
		},

		consumeTransferFromHash: () => {
			if (typeof window === 'undefined') {
				return false;
			}

			const { hash } = window.location;

			if (!hash.startsWith('#softai_auth=')) {
				return false;
			}

			try {
				const encodedPayload = decodeURIComponent(hash.slice('#softai_auth='.length));
				const parsed = JSON.parse(atob(encodedPayload));

				if (!parsed?.token) {
					return false;
				}

				this._writeLocal(STORAGE_KEYS.AUTH_TOKEN, parsed.token);
				this._writeCookie(
					COOKIE_CONFIG.AUTH_TOKEN.name,
					parsed.token,
					COOKIE_CONFIG.AUTH_TOKEN.maxAge,
				);

				if (parsed.refreshToken) {
					this._writeLocal(
						STORAGE_KEYS.AUTH_REFRESH_TOKEN,
						parsed.refreshToken,
					);
					this._writeCookie(
						COOKIE_CONFIG.REFRESH_TOKEN.name,
						parsed.refreshToken,
						COOKIE_CONFIG.REFRESH_TOKEN.maxAge,
					);
				}
			} catch {
				return false;
			}

			const nextUrl = `${window.location.pathname}${window.location.search}`;
			window.history.replaceState({}, document.title, nextUrl);

			return true;
		},
	};

	studio = {
		getState: () => {
			return this._readJsonLocal(STORAGE_KEYS.STUDIO_STATE);
		},

		setState: (state) => {
			this._writeJsonLocal(STORAGE_KEYS.STUDIO_STATE, state);
		},

		clearState: () => {
			this._removeLocal(STORAGE_KEYS.STUDIO_STATE);
		},

		markRestorePending: () => {
			this._writeSession(STORAGE_KEYS.STUDIO_RESTORE_FLAG, 'true');
		},

		consumeRestorePending: () => {
			const shouldRestore =
				this._readSession(STORAGE_KEYS.STUDIO_RESTORE_FLAG) === 'true';

			if (shouldRestore) {
				this._removeSession(STORAGE_KEYS.STUDIO_RESTORE_FLAG);
			}

			return shouldRestore;
		},

		markQuizAutoGeneratePending: () => {
			this._writeSession(STORAGE_KEYS.QUIZ_AUTO_GENERATE_FLAG, 'true');
		},

		isQuizAutoGeneratePending: () => {
			return this._readSession(STORAGE_KEYS.QUIZ_AUTO_GENERATE_FLAG) === 'true';
		},

		consumeQuizAutoGeneratePending: () => {
			const shouldGenerate =
				this._readSession(STORAGE_KEYS.QUIZ_AUTO_GENERATE_FLAG) === 'true';

			if (shouldGenerate) {
				this._removeSession(STORAGE_KEYS.QUIZ_AUTO_GENERATE_FLAG);
			}

			return shouldGenerate;
		},

		getQuizTransferCookie: () => {
			return this._readJsonCookie(COOKIE_CONFIG.QUIZ_STUDIO_TRANSFER.name);
		},

		clearQuizTransferCookie: () => {
			this._deleteCookie(COOKIE_CONFIG.QUIZ_STUDIO_TRANSFER.name);
		},
	};

	utm = {
		get: () => ({
			utm_campaign: this._readLocal(STORAGE_KEYS.UTM_CAMPAIGN),
			utm_medium: this._readLocal(STORAGE_KEYS.UTM_MEDIUM),
			utm_source: this._readLocal(STORAGE_KEYS.UTM_SOURCE),
			utm_term: this._readLocal(STORAGE_KEYS.UTM_TERM),
			utm_content: this._readLocal(STORAGE_KEYS.UTM_CONTENT),
			utm_fbclid: this._readLocal(STORAGE_KEYS.FBCLID),
			uid: this._readLocal(STORAGE_KEYS.UID),
			msclkid: this._readLocal(STORAGE_KEYS.MSCLKID),
		}),

		set: (params) => {
			if (params.utm_campaign) {
				this._writeLocal(STORAGE_KEYS.UTM_CAMPAIGN, params.utm_campaign);
			}
			if (params.utm_medium) {
				this._writeLocal(STORAGE_KEYS.UTM_MEDIUM, params.utm_medium);
			}
			if (params.utm_source) {
				this._writeLocal(STORAGE_KEYS.UTM_SOURCE, params.utm_source);
			}
			if (params.utm_term) {
				this._writeLocal(STORAGE_KEYS.UTM_TERM, params.utm_term);
			}
			if (params.utm_content) {
				this._writeLocal(STORAGE_KEYS.UTM_CONTENT, params.utm_content);
			}
			if (params.utm_fbclid) {
				this._writeLocal(STORAGE_KEYS.FBCLID, params.utm_fbclid);
			}
			if (params.uid) {
				this._writeLocal(STORAGE_KEYS.UID, params.uid);
			}
			if (params.msclkid) {
				this._writeLocal(STORAGE_KEYS.MSCLKID, params.msclkid);
			}
		},

		getBrowsingPersonId: () => {
			return this._readLocal(STORAGE_KEYS.BROWSING_PERSON);
		},

		setBrowsingPersonId: (id) => {
			if (!id) return;
			this._writeLocal(STORAGE_KEYS.BROWSING_PERSON, id);
		},
	};

	payment = {
		getTokenBaseline: () => {
			const value = this._readSession(STORAGE_KEYS.TOKEN_PAYMENT_BASELINE);
			if (value === null || value === '') {
				return null;
			}
			return Number(value);
		},

		setTokenBaseline: (tokens) => {
			this._writeSession(STORAGE_KEYS.TOKEN_PAYMENT_BASELINE, String(tokens));
		},

		clearTokenBaseline: () => {
			this._removeSession(STORAGE_KEYS.TOKEN_PAYMENT_BASELINE);
		},

		getError: () => {
			return this._readSession(STORAGE_KEYS.TOKEN_PAYMENT_ERROR);
		},

		setError: (message) => {
			this._writeSession(STORAGE_KEYS.TOKEN_PAYMENT_ERROR, message);
		},

		consumeError: () => {
			const message = this._readSession(STORAGE_KEYS.TOKEN_PAYMENT_ERROR);
			if (!message) {
				return null;
			}
			this._removeSession(STORAGE_KEYS.TOKEN_PAYMENT_ERROR);
			return message;
		},
	};
}

const storageManager = new StorageManager();
storageManager._hydrateAuthFromCookies();
export default storageManager;
